
export interface LocationEntity {
  name: string;
  query: string;
  lat?: number;
  lng?: number;
  placeId?: string;
  address?: string;
  rating?: number;
  photoRef?: string;
  source?: 'google' | 'osm';
}

export enum Vibe {
  FoodieFocused = "Foodie Focused",
  ArtsCulture = "Arts & Culture",
  SportingEvents = "Sporting Events",
  HealthWellness = "Health & Wellness",
  Historical = "Historical",
  Family = "Family",
  Nightlife = "Nightlife",
  Adventure = "Adventure",
  Shopping = "Shopping",
  Business = "Business"
}

export enum Palette {
  ObsidianNoir = "Obsidian Noir",
  VogueMinimal = "Vogue Minimal",
  MediterraneanSoul = "Mediterranean Soul",
  GoldenHour = "Golden Hour",
  WatercolorDream = "Watercolor Dream",
  TheHotelCollection = "The Hotel Collection"
}

export enum DossierTemplate {
  TheMinimalist = "The Minimalist",
  TheNoir = "The Noir",
  TheArtist = "The Artist",
  TheClassic = "The Classic",
  TheAdventurer = "The Adventurer"
}

export enum VisualAesthetic {
  EditorialVogue = "Editorial Vogue",
  HandPaintedWatercolor = "Hand-Painted Watercolor",
  HighEndPhotography = "High-End Photography", // Deprecated — kept for backward compat with saved trips
  ComicBookHero = "Comic Book Hero",
  TheHotelCollection = "The Hotel Collection"
}

export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'trialing' | 'none';
export type SubscriptionTier = 'free' | 'day_pass' | 'single' | 'weekend' | 'membership' | 'boutique' | 'partner' | 'global';

export interface CreditTransaction {
  id: string;
  description: string;
  amount: number; // negative = consumed, positive = added
  timestamp: number; // ms
}

export interface InvoiceRecord {
  id: string;
  date: string; // ISO string
  amount: number; // cents
  status: 'paid' | 'open' | 'void' | 'draft' | 'uncollectible';
  hostedUrl?: string;
}

export interface SubscriptionData {
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  subscriptionStatus: SubscriptionStatus;
  subscriptionTier?: string;        // 'atelier' | 'annual'
  currentPeriodStart?: string;       // ISO timestamp
  currentPeriodEnd?: string;         // ISO timestamp
  cancelAtPeriodEnd?: boolean;
  monthlyCreditsAllowance?: number;  // How many credits per billing cycle
  lastCreditReplenishment?: string;  // ISO timestamp
}

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  hasLicense: boolean;
  credits: number;
  lastSearchTimestamp: number | null; // Milliseconds
  createdAt?: number;
  // Subscription fields
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  subscriptionStatus?: 'active' | 'past_due' | 'canceled' | 'trialing' | 'none';
  subscriptionTier?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  monthlyCreditsAllowance?: number;
  lastCreditReplenishment?: string;
  paymentWarning?: string;           // Warning message for failed payments
  creditHistory?: CreditTransaction[];
  invoices?: InvoiceRecord[];
  role?: string;                     // Firestore rules require 'user' role for creation
}

export interface AtriumItem {
  id: string;
  title: string;
  sensoryDescription: string; // The "Kore" voice hook
  auraScore: number; // 1-100 Exclusivity rating
  category: string;
  matchReason: string;
}

export interface AirbnbListing {
  name: string;
  description: string;
  pricePerNight: string;
  bookingUrl: string;
  rating: string;
  superhost: boolean;
  imageUrl?: string;
  affiliateType?: 'stay22' | 'getyourguide' | 'travelpayouts' | 'tinggly' | 'saily' | 'amigoesim' | 'yesim' | 'gettransfer' | 'viator' | 'expedia';
}

export interface UserInput {
  clientName: string;
  companionName?: string; // Made optional
  homeAirport: string;
  isStaycation?: boolean;
  destination: string;
  preferredHotel?: string; // New Field
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
  vibe: Vibe[]; 
  vibeWeights?: Record<string, number>; 
  auraDescription?: string; 
  budgetTier?: number; 
  customLogo?: string; 
  travelerPhoto?: string; 
  agentFavorites?: string; 
  dietaryPreferences?: string[]; 
  cuisinePreferences?: string[]; // New Field
  // Agency / Business Card Fields
  agencyName?: string;
  agentName?: string;
  agentTitle?: string;
  agentEmail?: string;
  agentPhone?: string;
  licenseNumber?: string;
  contactInfo?: string; // Deprecated in favor of specific fields, kept for backward compat
  // Master Architect Fields
  templateId?: DossierTemplate;
  brandingPalette?: Palette;
  visualAesthetic?: VisualAesthetic;
  // Visibility Toggles
  includeLogistics?: boolean;
  includeBudget?: boolean;
  includeAirbnb?: boolean; // New Toggle
  // Knowledge Base
  knowledgeBase?: {
    data: string; 
    mimeType: string;
    fileName: string;
  };
  // Atrium Data
  atriumSelections?: AtriumItem[];
}

export interface DestinationRecommendation {
  name: string;
  country: string;
  reason: string;
  matchScore: number; 
  isAgentFavorite: boolean;
}

export interface LogisticsOption {
  name: string;
  description: string;
  priceRange: string;
  bookingQuery: string;
  bookingUrl?: string; 
  lat?: number;
  lng?: number;
  imageUrl?: string;
  affiliateType?: 'stay22' | 'getyourguide' | 'travelpayouts' | 'tinggly' | 'saily' | 'amigoesim' | 'yesim' | 'gettransfer' | 'viator' | 'expedia';
  // Agent Specifics
  bookingReference?: string;
  bookingConfirmationCode?: string;
  detailCode?: string;
  // Link validation metadata
  linkValidation?: LinkValidation;
  // Booking lock
  bookingLock?: BookingLock;
}

export interface BookingTiming {
  flightAdvice: string;
  hotelAdvice: string;
  peakWarning?: string;
  priceProjection: string;
}

export interface LogisticsData {
  flight: LogisticsOption;
  hotel: LogisticsOption;
  flightAlternatives?: LogisticsOption[];
  hotelAlternatives?: LogisticsOption[];
  bookingTiming?: BookingTiming;
  airbnbs?: AirbnbListing[];
}

export interface MapPoint {
  id: string;
  label: string;
  x: number;
  y: number;
  description: string;
  type?: string;
  name?: string;
  lat?: number;
  lng?: number;
}

export interface TimeSlot {
  activity: string;
  description: string;
  location: string;
  bookingUrl?: string;
  estimatedCost?: string; // New: e.g. "150 pp"
  affiliateType?: 'stay22' | 'getyourguide' | 'travelpayouts' | 'tinggly' | 'saily' | 'amigoesim' | 'yesim' | 'gettransfer' | 'viator' | 'expedia'; // New Field
  accessibilityNote?: string; // e.g. "Wheelchair accessible, elevator available"
  rating?: string;
  // Booking lock
  bookingLock?: BookingLock;
}

export interface DayItinerary {
  day: number;
  moodTitle: string;
  conciergeNotes: string;
  attire: string;
  transit: string;
  morning: TimeSlot;
  afternoon: TimeSlot;
  evening: TimeSlot;
  contingency?: TimeSlot; // New: "The Divergence" / Plan B
  imageUrl?: string;
  lat?: number;
  lng?: number;
  isPlaceholder?: boolean;
  dailyWeather?: string; // e.g. "72°F, partly cloudy, 20% rain"
}

export interface WeatherData {
  temperatureRange: string;
  condition: string;
  description: string;
  location: string;
  uvIndex?: string;
  rainfall?: string;
  humidity?: string;
}

export interface PackingItem {
  category: string;
  item: string;
  reason: string;
  brand?: string;
  estimatedPrice?: string;
}

export interface Budget {
  totalRange: string;
  breakdown: {
    category: string;
    low: number;
    high: number;
    currency: string;
  }[];
}

export interface DestinationInsights {
  overview: string;
  famousFor: string[];
  recentAwards: string[];
  keyLandmarks: string[];
  language: string;
  currency: string;
  timeDifference: string;
  emergencyContacts?: { label: string; number: string }[];
  localCustoms?: string[];
}

export interface LocalEvent {
  name: string;
  category: 'Concert' | 'Exhibition' | 'Market' | 'Festival' | 'Theater' | 'Sports' | 'Other';
  dates: string;          // Human-readable, e.g. "June 14" or "Through Aug 30"
  venue: string;
  neighborhood?: string;
  description: string;    // One sentence
  url?: string;           // Official event/venue page for more info
}

export interface ESimRecommendation {
  provider: string;
  planName: string;
  price: string;
  dataAllowance: string;
  validity: string;
  bookingUrl: string;
  reason: string;
  country?: string;
}

export interface TripData {
  itinerary: DayItinerary[];
  destinationSummary: string; 
  destinationInsights: DestinationInsights; 
  logistics: LogisticsData;
  mapPoints?: MapPoint[];
  weather: WeatherData;
  packingList: PackingItem[];
  budget: Budget;
  seed?: number; 
  // Agent Control Fields
  welcomeNote: string;
  termsAndConditions: string;
  // The Appendix
  digitalAppendix?: AtriumItem[];
  headerImageUrl?: string; 
  // Local happenings during the travel dates (search-grounded, real events)
  localEvents?: LocalEvent[];
  // Monetization
  eSimRecommendation?: ESimRecommendation; // New Field
  isInternational?: boolean; // New Field
  travelDocuments?: {
    visaRequired: boolean;
    visaType?: string;
    visaDetails?: string;
    passportValidity?: string;
    travelInsuranceRecommended: boolean;
    insuranceNotes?: string;
    vaccinationsRecommended?: string[];
    additionalDocuments?: string[];
  };
}

export interface SavedTrip extends TripData {
  id: string;
  userId: string;
  isPublic: boolean;
  createdAt: number;
  userInput: UserInput;
  collaborators?: Collaborator[];
  collaboratorEmails?: string[];  // denormalized for Firestore queries
  ownerName?: string;             // display name of trip owner
  ownerEmail?: string;            // email of trip owner
  _placeholder?: boolean; // true while trip is still being generated
}

export interface LinkValidation {
  originalUrl: string;
  validatedUrl: string;
  isOriginal: boolean;
  confidence: number;
  domain: string;
}

export interface TripSuggestion {
  moodTitle: string;
  description: string;
  activityName: string;
  attire: string;
  transit: string;
}

export interface Collaborator {
  email: string;
  displayName?: string;
  role: 'editor' | 'viewer';
  addedAt: string;        // ISO timestamp
  addedBy: string;        // uid of who invited them
  status: 'pending' | 'accepted';
}

export type BookingStatus = 'suggested' | 'interested' | 'confirmed' | 'booked';

export interface BookingLock {
  status: BookingStatus;
  lockedAt?: string;           // ISO timestamp when user confirmed interest
  bookingReference?: string;   // Optional reference number if actually booked
  notes?: string;              // User notes about this booking
  reminderSent?: boolean;
}

/**
 * A venue owned by (or partnered with) the hotel's parent group/portfolio —
 * e.g. Tavistock's Lake Nona Wave Hotel recommending Nami or Chroma, which
 * belong to the same umbrella company. The concierge generator prioritizes
 * these venues when they genuinely fit the guest's request.
 */
export interface PartnerVenue {
  name: string;
  category: 'Restaurant' | 'Bar' | 'Spa' | 'Golf' | 'Experience' | 'Retail' | 'Other';
  description?: string;       // What it is / why guests love it
  url?: string;               // The venue's own site (used as the accurate info link)
}

export interface WhiteLabelConfig {
  enabled: boolean;
  agencyName: string;
  agencyLogo?: string;        // URL or base64
  primaryColor?: string;       // Replaces gold (#D4AF37)
  accentColor?: string;        // Secondary accent
  tagline?: string;            // Replaces "Architecture of the Voyage"
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  footerText?: string;         // Custom footer for PDFs/emails
  // Hotel Concierge Studio fields (properties serving in-house guests)
  hotelName?: string;          // Property name shown on guest-facing plans
  hotelAddress?: string;       // Anchors guest recommendations to the property's neighborhood
  conciergeWelcome?: string;   // Welcome line printed at the top of guest plans
  partnerVenues?: PartnerVenue[]; // Portfolio/partner venues to feature in guest plans
}
